import React, { useMemo } from 'react';
import { Form as ReactForm } from 'react-bootstrap';
import * as Yup from 'yup';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import FormField from '../../form/FormField';
import { SearchResult } from './ManualSanctionCheckPage';
import { Button, SalvCard } from '@salvapp/ui-components';
import { partition } from 'lodash';
import {
  InternalApiCustomListScreeningField,
  InternalApiScreeningListType,
  InternalApiScreeningManualSearch,
  InternalApiScreeningManualSearchConfiguration,
  InternalApiScreeningManualSearchRecordEntityTypeEnum,
  InternalApiScreeningMatchType,
} from '../../api-internal';
import './SearchPanel.scss';
import CustomListSearchSelector, { ListSearchInput } from './CustomListSearchSelector';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { useMe } from '../../organisation/useMe';
import ScreeningTypeSelectorV2 from './ScreeningTypeSelectorV2';
import { screeningSearchApi } from '../../apis';
import { parseErrorMessage } from '../../common/errors/helpers';
import useCustomLists from '../../custom-lists/useCustomLists';
import { DowJonesCustomListCode } from '../configure/ScreeningDataSetup';
import { useQueryScreeningSelectorsGroups } from '../configure/lists/useQueryScreeningSelectors';

const validationSchema = Yup.object().shape({
  value: Yup.string().required('Required').min(3, 'Minimum length is 3'),
  djLists: Yup.array(),
  djListMatchType: Yup.string(),
  selectorCodes: Yup.array().of(Yup.string()),
  allProfilesSelected: Yup.boolean(),
  score: Yup.number(),
  prefix: Yup.number(),
  selectedListPaths: Yup.array().of(
    Yup.object().shape({
      id: Yup.string(),
      screeningFieldIds: Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.string().required('Path is required'),
            screeningSubType: Yup.string().required('Match type is required'),
            score: Yup.number().when('screeningSubType', {
              is: InternalApiScreeningMatchType.FUZZY_MATCH,
              then: (schema) => schema.min(90).max(100).required('Value must be between 90 and 100'),
              otherwise: (schema) => schema,
            }),
            prefixMatchLimit: Yup.number().when('screeningSubType', {
              is: InternalApiScreeningMatchType.STARTS_WITH,
              then: (schema) => schema.min(1).max(8).required('Value must be between 1 and 8'),
              otherwise: (schema) => schema.optional(),
            }),
          })
        )
        .min(1),
    })
  ),
});

export const onListToggle = (
  listId: string,
  values: ApiCustomListWithFields[],
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => {
  const listIsSelected = values.map((path) => path.id).includes(listId);
  if (listIsSelected) {
    setFieldValue(
      'selectedListPaths',
      values.filter((listPath) => listPath.id !== listId)
    );
  } else {
    setFieldValue('selectedListPaths', [
      ...values,
      {
        id: listId,
        screeningFieldIds: [],
      },
    ]);
  }
};

export const onPathToggle = (
  listId: string,
  path: InternalApiCustomListScreeningField,
  values: ApiCustomListWithFields[],
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => {
  const [current, others] = partition(values, (v) => v.id === listId);
  if (current[0].screeningFieldIds.map((p) => p.id).includes(path.id!)) {
    setFieldValue('selectedListPaths', [
      ...others,
      {
        id: listId,
        screeningFieldIds: current[0].screeningFieldIds.filter((p) => p.id !== path.id!),
      },
    ]);
  } else {
    setFieldValue('selectedListPaths', [
      ...others,
      {
        id: listId,
        screeningFieldIds: [
          ...current[0].screeningFieldIds,
          {
            id: path.id,
            screeningSubType: InternalApiScreeningMatchType.FUZZY_MATCH,
            score: 95,
          } as ApiCustomListWithFieldsScreeningFieldIdsInner,
        ],
      },
    ]);
  }
};

export const onPathTypeChange = (
  listId: string,
  pathId: string,
  matchType: InternalApiScreeningMatchType,
  values: ApiCustomListWithFields[],
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => {
  const [currentList, otherLists] = partition(values, (v) => v.id === listId);
  const [currentPath, otherPaths] = partition(currentList[0].screeningFieldIds, (v) => v.id === pathId);
  setFieldValue('selectedListPaths', [
    ...otherLists,
    {
      id: currentList[0].id,
      screeningFieldIds: [
        ...otherPaths,
        {
          id: currentPath[0].id,
          screeningSubType: matchType,
          score: matchType === InternalApiScreeningMatchType.FUZZY_MATCH ? 95 : undefined,
          prefixMatchLimit: matchType === InternalApiScreeningMatchType.STARTS_WITH ? 8 : undefined,
        },
      ],
    },
  ]);
};

export type SearchPanelValues = {
  value: string;
  recordEntityType?: {
    key: string;
    value: InternalApiScreeningManualSearchRecordEntityTypeEnum;
    label: string;
  };
  djLists: InternalApiScreeningListType[];
  djListMatchType: InternalApiScreeningMatchType;
  selectorCodes: string[];
  allProfilesSelected: boolean;
  score?: number;
  prefix?: number;
  selectedListPaths: ApiCustomListWithFields[];
};

export const RECORD_ENTITY_TYPES = [
  {
    key: 'ALL',
    value: InternalApiScreeningManualSearchRecordEntityTypeEnum.ALL,
    label: 'Individual & Business',
  },
  {
    key: 'PERSON',
    value: InternalApiScreeningManualSearchRecordEntityTypeEnum.PERSON,
    label: 'Individual',
  },
  {
    key: 'ENTITY',
    value: InternalApiScreeningManualSearchRecordEntityTypeEnum.ENTITY,
    label: 'Business',
  },
];

export interface ApiCustomListWithFields {
  id: string;
  screeningFieldIds: Array<ApiCustomListWithFieldsScreeningFieldIdsInner>;
}

export interface ApiCustomListWithFieldsScreeningFieldIdsInner {
  id: string;
  score?: number;
  prefixMatchLimit?: number;
  screeningSubType?: InternalApiScreeningMatchType;
}

interface Props {
  setResults: (result: SearchResult) => void;
}

const SearchPanel = ({ setResults }: Props) => {
  const { features } = useMe();
  const customLists = useCustomLists();
  const dowJonesListId = customLists.find((list) => list.code === DowJonesCustomListCode.DOW_JONES_SANCTION)?.id;
  const { data: screeningSelectorGroups } = useQueryScreeningSelectorsGroups(dowJonesListId);

  const [isDjEnabled, initialValues] = useMemo(() => {
    const isDjEnabled = isFeatureEnabled(ORGANISATION_FEATURE.DJ_SCREENING, features);
    return [
      isDjEnabled,
      {
        value: '',
        recordEntityType: RECORD_ENTITY_TYPES[0],
        djLists: [InternalApiScreeningListType.SANCTION],
        djListMatchType: InternalApiScreeningMatchType.FUZZY_MATCH,
        selectorCodes: screeningSelectorGroups?.map((group) => group.code) ?? [],
        allProfilesSelected: screeningSelectorGroups?.length === 0,
        score: 95,
        selectedListPaths: [],
      } as SearchPanelValues,
    ] as const;
  }, [features, screeningSelectorGroups]);

  const createDjConfigs = (
    type: InternalApiScreeningListType,
    matchType: InternalApiScreeningMatchType,
    score: number | undefined,
    prefixMatchLimit: number | undefined,
    allProfilesSelected: boolean,
    selectorGroupCodes: string[] | undefined
  ): InternalApiScreeningManualSearchConfiguration => {
    return {
      type: type,
      matchType: matchType,
      score: score,
      prefixMatchLimit: prefixMatchLimit,
      selectorGroupCodes:
        type === InternalApiScreeningListType.SANCTION ? (allProfilesSelected ? [] : selectorGroupCodes) : undefined,
    };
  };
  const createCustomListConfigs = (list: ApiCustomListWithFields): InternalApiScreeningManualSearchConfiguration[] => {
    return list.screeningFieldIds
      .map((screeningField) => {
        return {
          type: InternalApiScreeningListType.CUSTOM_LIST,
          matchType: screeningField.screeningSubType as unknown as InternalApiScreeningMatchType,
          listId: list.id,
          usableFieldId: screeningField.id,
          score: screeningField.score,
          prefixMatchLimit: screeningField.prefixMatchLimit,
        } as InternalApiScreeningManualSearchConfiguration;
      })
      .flat();
  };

  const submit = async (values: SearchPanelValues, { setStatus, setSubmitting }: FormikHelpers<SearchPanelValues>) => {
    try {
      const request = {
        value: values.value,
        recordEntityType: values.recordEntityType?.value,
        configurations: [
          ...values.djLists.map((type) =>
            createDjConfigs(
              type,
              values.djListMatchType,
              values.score,
              values.prefix,
              values.allProfilesSelected,
              values.selectorCodes
            )
          ),
          ...values.selectedListPaths.map((list) => createCustomListConfigs(list)).flat(),
        ],
      } as InternalApiScreeningManualSearch;
      const hits = await screeningSearchApi.searchInternal({ internalApiScreeningManualSearch: request });
      setResults({ name: values.value, hits: hits });
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    }
    setSubmitting(false);
  };

  const onInputChange =
    (values: ApiCustomListWithFields[], setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) =>
    (listId: string, pathId: string, value: number, type: ListSearchInput) => {
      const [currentList, otherLists] = partition(values, (v) => v.id === listId);
      const [currentPath, otherPaths] = partition(currentList[0].screeningFieldIds, (v) => v.id === pathId);
      setFieldValue('selectedListPaths', [
        ...otherLists,
        {
          id: currentList[0].id,
          screeningFieldIds: [
            ...otherPaths,
            {
              id: currentPath[0].id,
              screeningSubType: currentPath[0].screeningSubType,
              score: type === 'SCORE' ? value : undefined,
              prefixMatchLimit: type === 'PREFIX_LENGTH' ? value : undefined,
            },
          ],
        },
      ]);
    };

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={submit}
      initialValues={initialValues}
    >
      {({ isSubmitting, values, setFieldValue, resetForm }: FormikProps<SearchPanelValues>) => {
        return (
          <Form>
            <SalvCard>
              <SalvCard.Body style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                <FormField name='value' label='Value' placeholder='Search value' />
                {isDjEnabled && (
                  <SalvCard expansionOptions={{ isDefaultExpanded: true }}>
                    <SalvCard.Header>Dow Jones lists</SalvCard.Header>
                    <SalvCard.Body>
                      <ScreeningTypeSelectorV2
                        selectedTypes={values.djLists}
                        setSelectedTypes={(types) => setFieldValue('djLists', types)}
                        searchAllProfiles={values.allProfilesSelected}
                        setSearchAllProfiles={(value) => {
                          setFieldValue('allProfilesSelected', value);
                          if (value) {
                            setFieldValue('selectorCodes', []);
                          }
                        }}
                        screeningSelectorGroups={screeningSelectorGroups}
                        selectorGroupCodes={values.selectorCodes}
                        setSelectorGroupCodes={(value) => {
                          setFieldValue('selectorCodes', value);
                          if (value.length > 0) {
                            setFieldValue('allProfilesSelected', false);
                          }
                        }}
                        recordEntityTypeOption={values.recordEntityType}
                        setRecordEntityTypeOption={(option) => setFieldValue('recordEntityType', option)}
                        score={values.score}
                        setScore={(value) => {
                          setFieldValue('score', value);
                          setFieldValue('prefix', undefined);
                        }}
                        prefix={values.prefix}
                        setPrefix={(value) => {
                          setFieldValue('prefix', value);
                          setFieldValue('score', undefined);
                        }}
                        matchType={values.djListMatchType}
                        setMatchType={(type) => {
                          setFieldValue('djListMatchType', type);
                        }}
                      />
                    </SalvCard.Body>
                  </SalvCard>
                )}
                <CustomListSearchSelector
                  selectedListFields={values.selectedListPaths}
                  onListToggle={(listId: string) => onListToggle(listId, values.selectedListPaths, setFieldValue)}
                  onPathToggle={(listId: string, path: InternalApiCustomListScreeningField) =>
                    onPathToggle(listId, path, values.selectedListPaths, setFieldValue)
                  }
                  onPathTypeChange={(listId: string, pathId: string, matchType: InternalApiScreeningMatchType) =>
                    onPathTypeChange(listId, pathId, matchType, values.selectedListPaths, setFieldValue)
                  }
                  onInputChange={onInputChange(values.selectedListPaths, setFieldValue)}
                />
                <ReactForm.Group className='form-group pt-4 d-flex justify-content-end'>
                  <Button
                    variant='outline-secondary'
                    onClick={() => {
                      resetForm();
                      setResults({ hits: [] });
                    }}
                    className='me-3'
                  >
                    Clear
                  </Button>
                  <Button disabled={isSubmitting} type='submit' variant='primary'>
                    Search
                  </Button>
                </ReactForm.Group>
              </SalvCard.Body>
            </SalvCard>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SearchPanel;
